import { selectDigitalMap } from 'ducks/universal/digitalMap';
import { useSelector } from 'react-redux';
import styles from './LanguageSelect.module.css';
import { languageOptionMap } from 'lib/util/i18nHelpers';
import { useRouter } from 'next/router';
import { useUrl } from 'hooks/useUrl';
import { ContentLanguage } from 'models/settings';
import { useState, useRef, useEffect } from 'react';
import clsx from 'clsx';

export const LanguageSelect = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [newLanguage, setNewLanguage] = useState<ContentLanguage | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const digitalMap = useSelector(selectDigitalMap);
  const router = useRouter();

  const languageOptions = digitalMap?.display_settings?.language_menu_items ?? [];
  const baseUrl = useUrl('/maps/{{MAP_ID}}');
  const currentLanguage = digitalMap?.seo_params?.language;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLanguageChange = (newLanguage: ContentLanguage) => {
    if (newLanguage === currentLanguage) {
      setIsOpen(false);
      return;
    }
    const newLanguageIso = languageOptionMap[newLanguage].iso;

    let newUrl = baseUrl.replace(
      '{{MAP_ID}}',
      languageOptions.find((option) => option.language === newLanguage)?.digital_map_id ??
        digitalMap?.id ??
        ''
    );

    if (newUrl.includes('lng=')) {
      newUrl = newUrl.replace(/lng=[^&]+/, `lng=${newLanguageIso}`);
    } else {
      newUrl = `${newUrl}${newUrl.includes('?') ? '&' : '?'}lng=${newLanguageIso}`;
    }

    router.push(newUrl);
    setNewLanguage(newLanguage);
    setIsOpen(false);
  };

  return (
    <div
      className={clsx(styles.lang, {
        [styles.open]: isOpen,
        [styles.active]: !!newLanguage,
      })}
      onClick={() => setIsOpen(!isOpen)}
      ref={dropdownRef}
    >
      <img src="/static/images/map/globe.svg" alt="Language" />
      <span className={styles.selectedLanguage}>
        {newLanguage
          ? languageOptionMap[newLanguage].name
          : currentLanguage
          ? languageOptionMap[currentLanguage as ContentLanguage].name
          : 'English'}
      </span>
      {isOpen && (
        <div className={styles.dropdown}>
          {languageOptions.map((languageOption) => (
            <div
              key={languageOption.language}
              className={styles.option}
              onClick={() => handleLanguageChange(languageOption.language as ContentLanguage)}
            >
              {languageOptionMap[languageOption.language].name}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
